import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { V3_API_URL, fetcher } from 'views/TradingCompetitionV3/config'
import type { CompetitionRow } from 'views/TradingCompetitionV3/components/CompetitionCard'
import { V4_API_URL, V4_ENABLED } from 'views/TradingCompetitionV4/config'
import { mergeTokenCompetitions } from './competitions'
import { TOKEN_PAGES_API_URL } from './config'
import { filterItems, listedItems, sortItems } from './list'
import { mergeSuggestions, parseSearchQuery } from './search'
import type { Suggestion, TokenSearchResult } from './search'
import type { Address, ChartResponse, CompetitionWinner, ListResponse, ProjectInfoResponse, TfKey, TokenOverview, TokenPagesSettings, TradesResponse } from './types'

/** A missing token (404), a bad address (400) or a rate limit (429) is an answer; retrying it would only hammer the server. */
const noRetry404 = (err: Error, key: string, config: any, revalidate: any, { retryCount }: { retryCount: number }) => {
  if (['404', '400', '429'].includes(err.message) || retryCount >= 3) return
  setTimeout(() => revalidate({ retryCount }), 2000 * 2 ** retryCount)
}
const base = { revalidateOnFocus: false, onErrorRetry: noRetry404 }

export const useOverview = (address?: string) => useSWR<TokenOverview>(address ? `/api/tokens/${address}` : null, fetcher, { ...base, refreshInterval: 30_000 })
export const useChart = (address?: string, pool?: string | null, tf?: TfKey) =>
  useSWR<ChartResponse>(address && pool && tf ? `/api/tokens/${address}/chart?pool=${pool}&tf=${tf}` : null, fetcher, { ...base, refreshInterval: 30_000, keepPreviousData: true })
export const useTrades = (address?: string, pool?: string | null) =>
  useSWR<TradesResponse>(address && pool ? `/api/tokens/${address}/trades?pool=${pool}` : null, fetcher, { ...base, refreshInterval: 20_000, keepPreviousData: true })
/** `enabled` false: no request (the header's search asks for the list only once something is typed) */
export const useTokenList = (enabled = true) => useSWR<ListResponse>(enabled ? '/api/tokens/list' : null, fetcher, { ...base, refreshInterval: 60_000 })

/** Token suggestions for the search box: debounced 300 ms; `result` belongs to what is typed now (never to an older query). */
export function useTokenSearch(q: string) {
  const [applied, setApplied] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setApplied(q), 300)
    return () => clearTimeout(t)
  }, [q])
  const parsed = useMemo(() => parseSearchQuery(applied), [applied])
  const url = parsed.mode === 'invalid' ? null : `/api/tokens/search?q=${encodeURIComponent(parsed.mode === 'address' ? parsed.address : parsed.text)}`
  const r = useSWR<TokenSearchResult>(url, fetcher, { revalidateOnFocus: false, shouldRetryOnError: false })
  const typing = q.trim() !== applied.trim()
  const live = parseSearchQuery(q).mode !== 'invalid'
  return {
    result: !typing && live ? r.data : undefined,
    loading: live && (typing || (Boolean(url) && r.isLoading)),
    /** the server could not answer at all (502, network): shown like "busy", never as "no match" */
    failed: !typing && live && Boolean(r.error),
  }
}

export type TokenCompetition = CompetitionRow & { winners?: CompetitionWinner[] }
/**
 * Competitions whose project token is this token, on the current contract (V4 when configured) and the previous one (V3, tagged legacy).
 * Until 2026-10-07 only V3 was read, so the CADINU page said "Competitions 0" while V4 Competition #1 was live (site review 59 #1).
 * The API filters; the list is filtered again here so an older API cannot show another token's competition.
 */
export function useCompetitionsOf(address?: string) {
  const q = address ? `/competitions?status=all&projectToken=${address}` : null
  const r = useSWR<{ results: TokenCompetition[] }>(q ? `${V4_ENABLED ? V4_API_URL : V3_API_URL}${q}` : null, fetcher, { ...base, refreshInterval: 30_000 })
  const legacy = useSWR<{ results: TokenCompetition[] }>(q && V4_ENABLED ? `${V3_API_URL}${q}` : null, fetcher, { ...base, refreshInterval: 60_000, shouldRetryOnError: false })
  const rows = useMemo(() => mergeTokenCompetitions(r.data?.results, legacy.data?.results, address), [r.data, legacy.data, address])
  return { ...r, rows }
}
export const useProjectInfo = (address?: string) => useSWR<ProjectInfoResponse>(address ? `${TOKEN_PAGES_API_URL}/tokens/${address}` : null, fetcher, { ...base, refreshInterval: 120_000 })
export const useTokenPagesSettings = () => useSWR<TokenPagesSettings>(`${TOKEN_PAGES_API_URL}/settings`, fetcher, { ...base, refreshInterval: 60_000 })
/** The verified-project discount as the V4 API states it (the same key the create page reads); asked only when `enabled`. */
export type VerifiedDiscountTerms = { vouchers?: string; verifiedDiscount?: { on: boolean; blueBps: number; goldBps: number } }
export const useVerifiedDiscountTerms = (enabled: boolean) =>
  useSWR<VerifiedDiscountTerms>(enabled ? `${V4_API_URL}/config` : null, fetcher, { revalidateOnFocus: false, shouldRetryOnError: false })
export type { Address }

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
/**
 * The /tokens search for any search box (the header's): the hottest five local matches first (the hot list, then every other token page,
 * so a token with only a basic page is found too), then what the outside indexes list on BNB Smart Chain for the query, one row per
 * address, each marked verified, basic page, or low liquidity / unverified. Nothing is requested while empty.
 */
export function useTokenSuggestions(q: string) {
  const text = q.trim()
  const list = useTokenList(Boolean(text))
  const search = useTokenSearch(q)
  const isAddress = ADDRESS_RE.test(text)
  const listed = useMemo(() => listedItems(list.data), [list.data])
  const rows: Suggestion[] = useMemo(
    () => (text ? mergeSuggestions(sortItems(filterItems(listed, text), 'hot').slice(0, 5), search.result?.tokens ?? [], 8, listed) : []),
    [text, listed, search.result],
  )
  return { ...search, rows, isAddress, address: text, done: Boolean(search.result) && !search.loading, invalid: Boolean(text) && parseSearchQuery(text).mode === 'invalid' }
}
